import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, Navigate, useNavigate } from 'react-router-dom';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';

import 'leaflet/dist/leaflet.css';

// TypeScript interfaces
interface Achievement {
  id: number;
  name: string;
  icon: string;
  unlocked: boolean;
  xpReward: number;
}

interface DailyChallenge {
  id: number;
  title: string;
  progress: number;
  target: number;
  xpReward: number;
  completed: boolean;
}

interface Activity {
  type: 'order' | 'achievement' | 'streak';
  description: string;
  xp: number;
  points: number;
  time: string;
}

interface User {
  name: string;
  email: string;
  points: number;
  memberSince: string;
  totalOrders: number;
  favoriteLocation: string;
  streak: number;
  xp: number;
  level: number;
  xpToNextLevel: number;
  achievements: Achievement[];
  nextMilestone: {
    points: number;
    reward: string;
    progress: number;
  };
  dailyChallenges: DailyChallenge[];
  recentActivity: Activity[];
}

// Fix for default marker icons
import L from 'leaflet';
delete (L.Icon.Default.prototype as any)._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});
import { 
  Home, 
  MapPin, 
  CreditCard, 
  User,
  ShoppingCart,
  Clock,
  Phone,
  Navigation,
  Settings,
  LogOut,
  Mail,
  Lock,
  Eye,
  EyeOff
} from 'lucide-react';

// Mock data
const mockUser: User = {
  name: 'Jan Janssens',
  email: 'jan@example.com',
  points: 7,
  memberSince: '2023',
  totalOrders: 45,
  favoriteLocation: 'Poule & Poulette Antwerpen',
  streak: 7,
  xp: 1250,
  level: 8,
  xpToNextLevel: 160,
  achievements: [
    { id: 1, name: 'Eerste Bestelling', icon: '🎉', unlocked: true, xpReward: 50 },
    { id: 2, name: '10 Bestellingen', icon: '🏆', unlocked: true, xpReward: 100 },
    { id: 3, name: 'Week Streak', icon: '🔥', unlocked: true, xpReward: 200 },
    { id: 4, name: 'Gold Member', icon: '👑', unlocked: true, xpReward: 500 },
    { id: 5, name: '50 Bestellingen', icon: '💎', unlocked: false, xpReward: 1000 }
  ],
  nextMilestone: {
    points: 1500,
    reward: 'Gratis Dessert',
    progress: 83
  },
  dailyChallenges: [
    { id: 1, title: 'Bestel 2x vandaag', progress: 1, target: 2, xpReward: 50, completed: false },
    { id: 2, title: 'Probeer een nieuw gerecht', progress: 0, target: 1, xpReward: 75, completed: false },
    { id: 3, title: 'Deel je bestelling', progress: 0, target: 1, xpReward: 25, completed: false }
  ],
  recentActivity: [
    { type: 'order', description: 'Kip Menu + Friet', xp: 25, points: 25, time: '2 uur geleden' },
    { type: 'achievement', description: 'Gold Member behaald!', xp: 500, points: 0, time: '1 dag geleden' },
    { type: 'streak', description: '7 dagen streak!', xp: 100, points: 0, time: '3 dagen geleden' },
    { type: 'order', description: 'Burger + Cola', xp: 20, points: 20, time: '5 dagen geleden' }
  ]
};

const mockRestaurants = [
  {
    id: 1,
    name: 'Poule & Poulette Antwerpen Centrum',
    address: 'Meir 15, 2000 Antwerpen',
    distance: '0.8 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Drive-thru', 'Delivery', 'Takeaway'],
    phone: '+32 3 123 45 67',
    lat: 51.2194,
    lng: 4.4025
  },
  {
    id: 2,
    name: 'Poule & Poulette Gent Centrum',
    address: 'Veldstraat 25, 9000 Gent',
    distance: '2.1 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Delivery', 'Takeaway'],
    phone: '+32 9 123 45 67',
    lat: 51.0500,
    lng: 3.7300
  },
  {
    id: 3,
    name: 'Poule & Poulette Brussel Centrum',
    address: 'Nieuwstraat 10, 1000 Brussel',
    distance: '5.3 km',
    status: 'Closed',
    hours: '11:00 - 22:00',
    features: ['Takeaway'],
    phone: '+32 2 123 45 67',
    lat: 50.8503,
    lng: 4.3517
  },
  {
    id: 4,
    name: 'Poule & Poulette Leuven',
    address: 'Bondgenotenlaan 45, 3000 Leuven',
    distance: '8.7 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Drive-thru', 'Delivery', 'Takeaway'],
    phone: '+32 16 123 45 67',
    lat: 50.8798,
    lng: 4.7005
  },
  {
    id: 5,
    name: 'Poule & Poulette Brugge',
    address: 'Steenstraat 12, 8000 Brugge',
    distance: '12.4 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Delivery', 'Takeaway'],
    phone: '+32 50 123 45 67',
    lat: 51.2093,
    lng: 3.2247
  },
  {
    id: 6,
    name: 'Poule & Poulette Hasselt',
    address: 'Maastrichterstraat 78, 3500 Hasselt',
    distance: '15.2 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Drive-thru', 'Takeaway'],
    phone: '+32 11 123 45 67',
    lat: 50.9307,
    lng: 5.3325
  },
  {
    id: 7,
    name: 'Poule & Poulette Mechelen',
    address: 'Grote Markt 23, 2800 Mechelen',
    distance: '18.9 km',
    status: 'Open',
    hours: '11:00 - 22:00',
    features: ['Delivery', 'Takeaway'],
    phone: '+32 15 123 45 67',
    lat: 51.0259,
    lng: 4.4776
  },
  {
    id: 8,
    name: 'Poule & Poulette Aalst',
    address: 'Grote Markt 8, 9300 Aalst',
    distance: '22.1 km',
    status: 'Closed',
    hours: '11:00 - 22:00',
    features: ['Takeaway'],
    phone: '+32 53 123 45 67',
    lat: 50.9378,
    lng: 4.0400
  }
];

const mockRewards = [
  {
    id: 1,
    name: 'Gratis Friet',
    description: 'Gratis portie friet bij elke bestelling',
    points: 100,
    available: true
  },
  {
    id: 2,
    name: '2e Kip 50% Korting',
    description: '50% korting op je tweede kip bestelling',
    points: 200,
    available: true
  },
  {
    id: 3,
    name: 'Gratis Dessert',
    description: 'Gratis dessert naar keuze',
    points: 150,
    available: true
  }
];

// Authentication context
const AuthContext = React.createContext({
  isAuthenticated: false,
  login: (_email: string, _password: string) => Promise.resolve(false),
  logout: () => {},
  user: null as any
});

// Components
const Header = () => (
  <header className="header">
    <h1>POULE & POULETTE</h1>
    <p>REWARDS</p>
  </header>
);

const BottomNavigation = () => {
  const location = useLocation();
  const { isAuthenticated } = React.useContext(AuthContext);
  
  if (!isAuthenticated) return null;
  
  return (
    <nav className="bottom-nav">
      <Link to="/" className={`nav-item ${location.pathname === '/' ? 'active' : ''}`}>
        <Home />
        <span>Home</span>
      </Link>
      <Link to="/restaurants" className={`nav-item ${location.pathname === '/restaurants' ? 'active' : ''}`}>
        <MapPin />
        <span>Restaurants</span>
      </Link>
      <Link to="/loyalty" className={`nav-item ${location.pathname === '/loyalty' ? 'active' : ''}`}>
        <CreditCard />
        <span>Punten</span>
      </Link>
      <Link to="/profile" className={`nav-item ${location.pathname === '/profile' ? 'active' : ''}`}>
        <User />
        <span>Profiel</span>
      </Link>
    </nav>
  );
};

const LoginScreen = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const { login } = React.useContext(AuthContext);

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    try {
      const success = await login(email, password);
      if (!success) {
        setError('Ongeldige email of wachtwoord');
      }
    } catch (err) {
      setError('Er is een fout opgetreden');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <div className="login-card">
        <div className="login-header">
          <h1>Poule & Poulette</h1>
          <p>Log in op je account</p>
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <form onSubmit={handleLogin} className="login-form">
          <div className="form-group">
            <label className="form-label">Email</label>
            <div className="input-group">
              <Mail size={20} className="input-icon" />
              <input
                type="email"
                className="form-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="jan@example.com"
                required
              />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Wachtwoord</label>
            <div className="input-group">
              <Lock size={20} className="input-icon" />
              <input
                type={showPassword ? 'text' : 'password'}
                className="form-input"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
              />
              <button
                type="button"
                className="password-toggle"
                onClick={() => setShowPassword(!showPassword)}
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            className="btn btn-primary login-btn"
            disabled={isLoading}
          >
            {isLoading ? 'Inloggen...' : 'Inloggen'}
          </button>
        </form>

        <div className="login-footer">
          <p>Nog geen account? <a href="#" className="text-primary">Registreer hier</a></p>
          <p><a href="#" className="text-secondary">Wachtwoord vergeten?</a></p>
        </div>
      </div>
    </div>
  );
};

const HomeScreen = () => {
  const { isAuthenticated } = React.useContext(AuthContext);
  const navigate = useNavigate();

  const handleMijnPuntenClick = () => {
    if (isAuthenticated) {
      navigate('/loyalty');
    } else {
      navigate('/login');
    }
  };

  const handleBestelNuClick = () => {
    if (isAuthenticated) {
      navigate('/order');
    } else {
      navigate('/login');
    }
  };

  return (
    <div className="home-screen">
      {/* Hero Section */}
      <div className="hero-section">
        <div className="hero-content">
          <div className="hero-badge">
            <span>👑 GOLD MEMBER</span>
          </div>
          <h1 className="hero-title">Welkom terug, {mockUser.name.split(' ')[0]}!</h1>
          <p className="hero-subtitle">Je hebt vandaag al {mockUser.streak} dagen op rij besteld</p>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="quick-actions-section">
        <div className="action-grid">
          <button className="action-card primary" onClick={handleBestelNuClick}>
            <div className="action-icon">🍗</div>
            <div className="action-content">
              <h3>Bestel Nu</h3>
              <p>Snelle bestelling</p>
            </div>
            <div className="action-arrow">→</div>
          </button>
          
          <button className="action-card" onClick={handleMijnPuntenClick}>
            <div className="action-icon">⭐</div>
            <div className="action-content">
              <h3>Mijn Punten</h3>
              <p>Bekijk beloningen</p>
            </div>
            <div className="action-arrow">→</div>
          </button>
          
          <button className="action-card" onClick={() => navigate('/restaurants')}>
            <div className="action-icon">📍</div>
            <div className="action-content">
              <h3>Restaurants</h3>
              <p>Vind locaties</p>
            </div>
            <div className="action-arrow">→</div>
          </button>
          
          <button className="action-card" onClick={() => {
            if (navigator.share) {
              navigator.share({
                title: 'POULE & POULETTE REWARDS',
                text: `Ik heb ${mockUser.points} punten verzameld bij Poule & Poulette! 🍗`,
                url: window.location.href
              });
            } else {
              navigator.clipboard.writeText(`Ik heb ${mockUser.points} punten verzameld bij Poule & Poulette! 🍗`);
              alert('Link gekopieerd naar klembord!');
            }
          }}>
            <div className="action-icon">📤</div>
            <div className="action-content">
              <h3>Delen</h3>
              <p>Met vrienden</p>
            </div>
            <div className="action-arrow">→</div>
          </button>
        </div>
      </div>

      {/* Offers Section */}
      <div className="offers-section">
        <div className="section-header">
          <h2>🔥 Speciale Aanbiedingen</h2>
          <span className="section-badge">LIMITED TIME</span>
        </div>
        
        <div className="offers-grid">
          <div className="offer-card featured">
            <div className="offer-image">🍗</div>
            <div className="offer-content">
              <div className="offer-badge">HOT DEAL</div>
              <h3>2e Kip 50% Korting!</h3>
              <p>Geldig tot 31 december 2024</p>
              <div className="offer-timer">
                <span>⏰ Nog 15 dagen geldig</span>
              </div>
            </div>
            <button className="offer-btn">Bestel Nu</button>
          </div>
          
          <div className="offer-card">
            <div className="offer-image">🍟</div>
            <div className="offer-content">
              <div className="offer-badge">GOLD ONLY</div>
              <h3>Gratis Friet</h3>
              <p>Bij elke bestelling</p>
            </div>
            <button className="offer-btn">Bekijk</button>
          </div>
        </div>
      </div>

      {/* Progress Section */}
      <div className="progress-section">
        <div className="section-header">
          <h2>📊 Je Voortgang</h2>
        </div>
        
        <div className="progress-grid">
          <div className="progress-card">
            <div className="progress-header">
              <div className="progress-icon">🎯</div>
              <div className="progress-info">
                <h3>Volgende Milestone</h3>
                <p>{mockUser.points} / {mockUser.nextMilestone.points} punten</p>
              </div>
            </div>
            <div className="progress-bar">
              <div className="progress-fill" style={{width: `${mockUser.nextMilestone.progress}%`}}></div>
            </div>
            <div className="progress-reward">
              <span>🎁 {mockUser.nextMilestone.reward}</span>
            </div>
          </div>
          
          <div className="progress-card">
            <div className="progress-header">
              <div className="progress-icon">🔥</div>
              <div className="progress-info">
                <h3>Streak</h3>
                <p>{mockUser.streak} dagen op rij</p>
              </div>
            </div>
            <div className="streak-visual">
              {[...Array(7)].map((_, i) => (
                <div key={i} className={`streak-day ${i < mockUser.streak ? 'active' : ''}`}>
                  {i < mockUser.streak ? '🔥' : '⚪'}
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Recent Activity */}
      <div className="activity-section">
        <div className="section-header">
          <h2>📋 Recente Activiteit</h2>
          <button className="view-all-btn">Bekijk Alles</button>
        </div>
        
        <div className="activity-list">
          {mockUser.recentActivity.slice(0, 3).map((activity, index) => (
            <div key={index} className="activity-item">
              <div className="activity-icon">
                {activity.type === 'order' && '🍽️'}
                {activity.type === 'achievement' && '🏆'}
                {activity.type === 'streak' && '🔥'}
              </div>
              <div className="activity-content">
                <h4>{activity.description}</h4>
                <span className="activity-time">{activity.time}</span>
              </div>
              <div className="activity-reward">
                {activity.xp > 0 && <span className="xp-badge">+{activity.xp} XP</span>}
                {activity.points > 0 && <span className="points-badge">+{activity.points} punten</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Achievements Preview */}
      <div className="achievements-section">
        <div className="section-header">
          <h2>🏆 Achievements</h2>
          <span className="achievements-count">{mockUser.achievements.filter(a => a.unlocked).length}/{mockUser.achievements.length}</span>
        </div>
        
        <div className="achievements-preview">
          {mockUser.achievements.slice(0, 4).map(achievement => (
            <div key={achievement.id} className={`achievement-preview ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
              <div className="achievement-icon">{achievement.icon}</div>
              <div className="achievement-info">
                <h4>{achievement.name}</h4>
                <span className="achievement-xp">+{achievement.xpReward} XP</span>
              </div>
              {achievement.unlocked && <div className="unlocked-indicator">✅</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const RestaurantsScreen = () => {
  const navigate = useNavigate();
  const [filter, setFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedRestaurant, setSelectedRestaurant] = useState<any>(null);

  const filteredRestaurants = mockRestaurants.filter(restaurant => {
    const matchesSearch = restaurant.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         restaurant.address.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesFilter = filter === 'all' || 
                         (filter === 'open' && restaurant.status === 'Open') ||
                         (filter === 'drive-thru' && restaurant.features.includes('Drive-thru')) ||
                         (filter === 'delivery' && restaurant.features.includes('Delivery'));
    
    return matchesSearch && matchesFilter;
  });

  const handleRestaurantClick = (restaurant: any) => {
    setSelectedRestaurant(restaurant);
    console.log('Restaurant clicked:', restaurant.name);
  };

  // Debug logging
  useEffect(() => {
    console.log('Filtered restaurants:', filteredRestaurants);
    console.log('Restaurants with coordinates:', filteredRestaurants.map(r => ({
      name: r.name,
      lat: r.lat,
      lng: r.lng
    })));
  }, [filteredRestaurants]);

  return (
    <div className="restaurants-page">
      {/* Header */}
              <div className="restaurants-header">
          <h2>VIND EEN POULE & POULETTE RESTAURANT</h2>
          <p>Ontdek onze restaurants in heel België</p>
        </div>

      {/* Search and Filters */}
      <div className="restaurants-controls">
        <div className="search-container">
          <input
            type="text"
            placeholder="Zoek op stad, postcode of adres..."
            className="search-input"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        
        <div className="filters-container">
          <button 
            className={`filter-btn ${filter === 'all' ? 'active' : ''}`}
            onClick={() => setFilter('all')}
          >
            Alle restaurants
          </button>
          <button 
            className={`filter-btn ${filter === 'open' ? 'active' : ''}`}
            onClick={() => setFilter('open')}
          >
            Nu open
          </button>
          <button 
            className={`filter-btn ${filter === 'drive-thru' ? 'active' : ''}`}
            onClick={() => setFilter('drive-thru')}
          >
            Drive-thru
          </button>
          <button 
            className={`filter-btn ${filter === 'delivery' ? 'active' : ''}`}
            onClick={() => setFilter('delivery')}
          >
            Delivery
          </button>
        </div>
      </div>

      {/* Main Content - Map and List Side by Side */}
      <div className="restaurants-layout">
        {/* Map Section */}
        <div className="map-section">
          <div className="map-container">
            <div className="map-header">
              <span>{filteredRestaurants.length} restaurants gevonden</span>
            </div>
            <MapContainer
              center={[50.8503, 4.3517]}
              zoom={8}
              style={{ height: '100%', width: '100%' }}
              zoomControl={true}
              scrollWheelZoom={true}
              doubleClickZoom={true}
              dragging={true}
            >
              <TileLayer
                url="https://{s}.tile.openstreetmap.fr/hot/{z}/{x}/{y}.png"
                attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                maxZoom={19}
              />
              {filteredRestaurants.map(restaurant => (
                <Marker
                  key={restaurant.id}
                  position={[restaurant.lat, restaurant.lng]}
                  eventHandlers={{
                    click: () => handleRestaurantClick(restaurant)
                  }}
                >
                  <Popup>
                    <div className="restaurant-popup">
                      <h3>{restaurant.name}</h3>
                      <p><strong>Adres:</strong> {restaurant.address}</p>
                      <p><strong>Status:</strong> <span style={{color: restaurant.status === 'Open' ? '#27AE60' : '#E74C3C', fontWeight: 'bold'}}>{restaurant.status}</span></p>
                      <p><strong>Openingstijden:</strong> {restaurant.hours}</p>
                      <p><strong>Afstand:</strong> {restaurant.distance}</p>
                      <p><strong>Features:</strong> {restaurant.features.join(', ')}</p>
                      <div className="popup-actions">
                        <button onClick={() => window.open(`tel:${restaurant.phone}`)}>
                          <Phone size={14} /> Bellen
                        </button>
                        <button onClick={() => window.open(`https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}`)}>
                          <Navigation size={14} /> Route
                        </button>
                        <button onClick={() => navigate('/order')}>
                          <ShoppingCart size={14} /> Bestellen
                        </button>
                      </div>
                    </div>
                  </Popup>
                </Marker>
              ))}
            </MapContainer>
          </div>
        </div>

        {/* Restaurant List Section */}
        <div className="restaurants-list-section">
          <div className="list-header">
            <h3>Restaurants ({filteredRestaurants.length})</h3>
          </div>
          <div className="restaurants-list">
            {filteredRestaurants.map(restaurant => (
              <div 
                key={restaurant.id} 
                className={`restaurant-card ${selectedRestaurant?.id === restaurant.id ? 'selected' : ''}`}
                onClick={() => handleRestaurantClick(restaurant)}
              >
                <div className="restaurant-info">
                  <div className="restaurant-main">
                    <h4>{restaurant.name}</h4>
                    <p className="restaurant-address">{restaurant.address}</p>
                    <p className="restaurant-distance">{restaurant.distance}</p>
                  </div>
                  <div className="restaurant-status">
                    <span className={`status-badge ${restaurant.status === 'Open' ? 'open' : 'closed'}`}>
                      {restaurant.status}
                    </span>
                  </div>
                </div>
                
                <div className="restaurant-details">
                  <div className="restaurant-hours">
                    <Clock size={14} />
                    <span>{restaurant.hours}</span>
                  </div>
                  <div className="restaurant-features">
                    {restaurant.features.map(feature => (
                      <span key={feature} className="feature-badge">
                        {feature}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="restaurant-actions">
                  <button className="action-btn" onClick={(e) => { e.stopPropagation(); window.open(`tel:${restaurant.phone}`); }}>
                    <Phone size={16} />
                    <span>Bellen</span>
                  </button>
                  <button className="action-btn" onClick={(e) => { e.stopPropagation(); window.open(`https://www.google.com/maps/dir/?api=1&destination=${restaurant.lat},${restaurant.lng}`); }}>
                    <Navigation size={16} />
                    <span>Route</span>
                  </button>
                  <button className="action-btn" onClick={(e) => { e.stopPropagation(); navigate('/order'); }}>
                    <ShoppingCart size={16} />
                    <span>Bestellen</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const LoyaltyScreen = () => {
  const { user } = React.useContext(AuthContext) as { user: User };
  
  return (
    <div className="main-content">
      {/* XP & Level Section */}
      <div className="card xp-card">
        <div className="xp-header">
          <div className="level-badge">
            <span className="level-number">{user.level}</span>
            <span className="level-text">LEVEL</span>
          </div>
          <div className="xp-info">
            <h3>Level {user.level} - {user.level === 8 ? 'Gold' : user.level === 5 ? 'Silver' : 'Bronze'} Member</h3>
            <div className="xp-progress">
              <div className="xp-bar">
                <div className="xp-fill" style={{width: `${((user.xp % 400) / 400) * 100}%`}}></div>
              </div>
              <span className="xp-text">{user.xpToNextLevel} XP naar level {user.level + 1}</span>
            </div>
          </div>
        </div>
        <div className="xp-stats">
          <div className="stat-item">
            <span className="stat-value">{user.xp}</span>
            <span className="stat-label">Totaal XP</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{user.points}</span>
            <span className="stat-label">Punten</span>
          </div>
          <div className="stat-item">
            <span className="stat-value">{user.streak}</span>
            <span className="stat-label">Dagen Streak</span>
          </div>
        </div>
      </div>

      {/* Daily Challenges */}
      <div className="card">
        <h3>🎯 Dagelijkse Uitdagingen</h3>
        <div className="challenges-grid">
          {user.dailyChallenges.map(challenge => (
            <div key={challenge.id} className={`challenge-card ${challenge.completed ? 'completed' : ''}`}>
              <div className="challenge-header">
                <h4>{challenge.title}</h4>
                <span className="challenge-xp">+{challenge.xpReward} XP</span>
              </div>
              <div className="challenge-progress">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{width: `${(challenge.progress / challenge.target) * 100}%`}}
                  ></div>
                </div>
                <span className="progress-text">{challenge.progress}/{challenge.target}</span>
              </div>
              {challenge.completed && <div className="completed-badge">✅ Voltooid</div>}
            </div>
          ))}
        </div>
      </div>

      {/* Available Rewards */}
      <div className="card">
        <h3>🎁 Beschikbare Beloningen</h3>
        <div className="rewards-grid">
          {mockRewards.map(reward => (
            <div key={reward.id} className="reward-card">
              <div className="reward-icon">🎁</div>
              <h4>{reward.name}</h4>
              <p>{reward.description}</p>
              <div className="reward-points">{reward.points} punten</div>
              <button className={`btn ${reward.available ? 'btn-primary' : 'btn-disabled'}`}>
                {reward.available ? 'Inwisselen' : 'Niet beschikbaar'}
              </button>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Activity */}
      <div className="card">
        <h3>📊 Recente Activiteit</h3>
        <div className="activity-list">
          {user.recentActivity.map((activity, index) => (
            <div key={index} className="activity-item">
              <div className="activity-icon">
                {activity.type === 'order' && '🍽️'}
                {activity.type === 'achievement' && '🏆'}
                {activity.type === 'streak' && '🔥'}
              </div>
              <div className="activity-content">
                <div className="activity-description">{activity.description}</div>
                <div className="activity-time">{activity.time}</div>
              </div>
              <div className="activity-rewards">
                {activity.xp > 0 && <span className="xp-reward">+{activity.xp} XP</span>}
                {activity.points > 0 && <span className="points-reward">+{activity.points} punten</span>}
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Achievements */}
      <div className="card">
        <h3>🏆 Achievements</h3>
        <div className="achievements-grid">
          {user.achievements.map(achievement => (
            <div key={achievement.id} className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
              <div className="achievement-icon">{achievement.icon}</div>
              <div className="achievement-info">
                <h4>{achievement.name}</h4>
                <span className="achievement-xp">+{achievement.xpReward} XP</span>
              </div>
              {achievement.unlocked && <div className="unlocked-badge">✅</div>}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

// Mock menu data
const mockMenu = [
  {
    id: 1,
    category: 'Kip Gerechten',
    items: [
      { id: 1, name: 'Kip Burger', description: 'Gegrilde kipfilet met verse groenten', price: 8.50, image: '🍔', points: 25 },
      { id: 2, name: 'Kip Wings', description: '6 stuks kipvleugels met saus', price: 7.50, image: '🍗', points: 20 },
      { id: 3, name: 'Kip Salade', description: 'Verse salade met gegrilde kip', price: 9.00, image: '🥗', points: 30 }
    ]
  },
  {
    id: 2,
    category: 'Friet & Sides',
    items: [
      { id: 4, name: 'Friet Portie', description: 'Grote portie verse friet', price: 3.50, image: '🍟', points: 10 },
      { id: 5, name: 'Onion Rings', description: '6 stuks krokante uienringen', price: 4.00, image: '🧅', points: 15 },
      { id: 6, name: 'Coleslaw', description: 'Verse koolsalade', price: 2.50, image: '🥬', points: 8 }
    ]
  },
  {
    id: 3,
    category: 'Dranken',
    items: [
      { id: 7, name: 'Cola', description: 'Frisdrank 330ml', price: 2.50, image: '🥤', points: 5 },
      { id: 8, name: 'Koffie', description: 'Verse koffie', price: 2.00, image: '☕', points: 5 },
      { id: 9, name: 'Water', description: 'Mineraalwater 500ml', price: 1.50, image: '💧', points: 3 }
    ]
  }
];

const OrderScreen = () => {
  const navigate = useNavigate();
  const [cart, setCart] = useState<Array<{id: number, name: string, price: number, quantity: number, points: number}>>([]);
  const [activeCategory, setActiveCategory] = useState(1);
  const [showCart, setShowCart] = useState(false);

  const addToCart = (item: any) => {
    setCart(prevCart => {
      const existingItem = prevCart.find(cartItem => cartItem.id === item.id);
      if (existingItem) {
        return prevCart.map(cartItem =>
          cartItem.id === item.id
            ? { ...cartItem, quantity: cartItem.quantity + 1 }
            : cartItem
        );
      } else {
        return [...prevCart, { ...item, quantity: 1 }];
      }
    });
  };

  const removeFromCart = (itemId: number) => {
    setCart(prevCart => prevCart.filter(item => item.id !== itemId));
  };

  const updateQuantity = (itemId: number, quantity: number) => {
    if (quantity <= 0) {
      removeFromCart(itemId);
    } else {
      setCart(prevCart =>
        prevCart.map(item =>
          item.id === itemId ? { ...item, quantity } : item
        )
      );
    }
  };

  const getTotalPrice = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const getTotalPoints = () => {
    return cart.reduce((total, item) => total + (item.points * item.quantity), 0);
  };

  const handleCheckout = () => {
    alert(`Bestelling geplaatst! Je verdient ${getTotalPoints()} punten.`);
    setCart([]);
    navigate('/');
  };

  return (
    <div className="order-page">
      {/* Header */}
      <div className="order-header">
        <button className="back-btn" onClick={() => navigate('/')}>
          ← Terug
        </button>
        <h2>🍗 Bestellen</h2>
        <button className="cart-btn" onClick={() => setShowCart(!showCart)}>
          🛒 {cart.length > 0 && <span className="cart-count">{cart.reduce((sum, item) => sum + item.quantity, 0)}</span>}
        </button>
      </div>

      <div className="order-layout">
        {/* Menu Section */}
        <div className="menu-section">
          {/* Categories */}
          <div className="categories-tabs">
            {mockMenu.map(category => (
              <button
                key={category.id}
                className={`category-tab ${activeCategory === category.id ? 'active' : ''}`}
                onClick={() => setActiveCategory(category.id)}
              >
                {category.category}
              </button>
            ))}
          </div>

          {/* Menu Items */}
          <div className="menu-items">
            {mockMenu.find(cat => cat.id === activeCategory)?.items.map(item => (
              <div key={item.id} className="menu-item">
                <div className="item-image">{item.image}</div>
                <div className="item-info">
                  <h3>{item.name}</h3>
                  <p>{item.description}</p>
                  <div className="item-meta">
                    <span className="item-price">€{item.price.toFixed(2)}</span>
                    <span className="item-points">+{item.points} punten</span>
                  </div>
                </div>
                <button className="add-btn" onClick={() => addToCart(item)}>
                  +
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Cart Section */}
        {showCart && (
          <div className="cart-section">
            <div className="cart-header">
              <h3>🛒 Winkelwagen</h3>
              <button className="close-cart" onClick={() => setShowCart(false)}>×</button>
            </div>
            
            {cart.length === 0 ? (
              <div className="empty-cart">
                <p>Je winkelwagen is leeg</p>
                <span>Voeg items toe om te bestellen</span>
              </div>
            ) : (
              <>
                <div className="cart-items">
                  {cart.map(item => (
                    <div key={item.id} className="cart-item">
                      <div className="cart-item-info">
                        <h4>{item.name}</h4>
                        <span className="cart-item-price">€{item.price.toFixed(2)}</span>
                      </div>
                      <div className="cart-item-controls">
                        <button onClick={() => updateQuantity(item.id, item.quantity - 1)}>-</button>
                        <span>{item.quantity}</span>
                        <button onClick={() => updateQuantity(item.id, item.quantity + 1)}>+</button>
                      </div>
                    </div>
                  ))}
                </div>
                
                <div className="cart-summary">
                  <div className="summary-row">
                    <span>Totaal:</span>
                    <span>€{getTotalPrice().toFixed(2)}</span>
                  </div>
                  <div className="summary-row">
                    <span>Punten te verdienen:</span>
                    <span className="points-earned">+{getTotalPoints()}</span>
                  </div>
                </div>
                
                <button className="checkout-btn" onClick={handleCheckout}>
                  Bestellen - €{getTotalPrice().toFixed(2)}
                </button>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const ProfileScreen = () => {
  const { logout, user } = React.useContext(AuthContext) as { logout: () => void, user: User };
  const [activeTab, setActiveTab] = useState('overview');

  return (
    <div className="main-content">
      {/* Profile Header */}
      <div className="profile-header">
        <div className="profile-avatar">
          <div className="avatar-circle">
            <span className="avatar-text">{user.name.split(' ').map(n => n[0]).join('')}</span>
          </div>
          <div className="level-indicator">
            <span className="level-badge-small">{user.level}</span>
          </div>
        </div>
        <div className="profile-info">
          <h2>{user.name}</h2>
          <p className="profile-email">{user.email}</p>
          <div className="member-since">
            <span>👑 {user.level === 8 ? 'Gold' : user.level === 5 ? 'Silver' : 'Bronze'} Member</span>
            <span>• Lid sinds {user.memberSince}</span>
          </div>
        </div>
      </div>

      {/* Stats Overview */}
      <div className="stats-overview">
        <div className="stat-card">
          <div className="stat-icon">🍽️</div>
          <div className="stat-content">
            <div className="stat-value">{user.totalOrders}</div>
            <div className="stat-label">Bestellingen</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">⭐</div>
          <div className="stat-content">
            <div className="stat-value">{user.points}</div>
            <div className="stat-label">Punten</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🔥</div>
          <div className="stat-content">
            <div className="stat-value">{user.streak}</div>
            <div className="stat-label">Dagen Streak</div>
          </div>
        </div>
        <div className="stat-card">
          <div className="stat-icon">🏆</div>
          <div className="stat-content">
            <div className="stat-value">{user.xp}</div>
            <div className="stat-label">Totaal XP</div>
          </div>
        </div>
      </div>

      {/* Profile Tabs */}
      <div className="profile-tabs">
        <button 
          className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
          onClick={() => setActiveTab('overview')}
        >
          📊 Overzicht
        </button>
        <button 
          className={`tab-btn ${activeTab === 'achievements' ? 'active' : ''}`}
          onClick={() => setActiveTab('achievements')}
        >
          🏆 Achievements
        </button>
        <button 
          className={`tab-btn ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          ⚙️ Instellingen
        </button>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="tab-content">
          {/* Favorite Location */}
          <div className="card location-card">
            <div className="card-header">
              <h3>📍 Favoriete Locatie</h3>
              <span className="location-badge">Meest bezocht</span>
            </div>
            <div className="location-info">
              <div className="location-icon">🏪</div>
              <div className="location-details">
                <h4>{user.favoriteLocation}</h4>
                <p>Laatste bezoek: 2 dagen geleden</p>
              </div>
            </div>
          </div>

          {/* Recent Activity Summary */}
          <div className="card">
            <h3>📈 Recente Activiteit</h3>
            <div className="activity-summary">
              {user.recentActivity.slice(0, 3).map((activity, index) => (
                <div key={index} className="activity-summary-item">
                  <div className="activity-icon-small">
                    {activity.type === 'order' && '🍽️'}
                    {activity.type === 'achievement' && '🏆'}
                    {activity.type === 'streak' && '🔥'}
                  </div>
                  <div className="activity-text">{activity.description}</div>
                  <div className="activity-time-small">{activity.time}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Quick Actions */}
          <div className="card">
            <h3>⚡ Snelle Acties</h3>
            <div className="quick-actions">
              <button className="quick-action-btn">
                <span>📱</span>
                <span>Bestel Nu</span>
              </button>
              <button className="quick-action-btn">
                <span>🎁</span>
                <span>Beloningen</span>
              </button>
              <button className="quick-action-btn">
                <span>🗺️</span>
                <span>Restaurants</span>
              </button>
              <button className="quick-action-btn">
                <span>📤</span>
                <span>Delen</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {activeTab === 'achievements' && (
        <div className="tab-content">
          <div className="card">
            <h3>🏆 Mijn Achievements</h3>
            <div className="achievements-summary">
              <div className="achievement-stats">
                <div className="achievement-stat">
                  <span className="stat-number">{user.achievements.filter(a => a.unlocked).length}</span>
                  <span className="stat-text">Behaald</span>
                </div>
                <div className="achievement-stat">
                  <span className="stat-number">{user.achievements.length}</span>
                  <span className="stat-text">Totaal</span>
                </div>
                <div className="achievement-stat">
                  <span className="stat-number">{user.achievements.reduce((sum, a) => sum + (a.unlocked ? a.xpReward : 0), 0)}</span>
                  <span className="stat-text">XP Verdiend</span>
                </div>
              </div>
            </div>
            <div className="achievements-grid">
              {user.achievements.map(achievement => (
                <div key={achievement.id} className={`achievement-card ${achievement.unlocked ? 'unlocked' : 'locked'}`}>
                  <div className="achievement-icon">{achievement.icon}</div>
                  <div className="achievement-info">
                    <h4>{achievement.name}</h4>
                    <span className="achievement-xp">+{achievement.xpReward} XP</span>
                  </div>
                  {achievement.unlocked && <div className="unlocked-badge">✅</div>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {activeTab === 'settings' && (
        <div className="tab-content">
          <div className="card">
            <h3>⚙️ Instellingen</h3>
            <div className="settings-list">
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">👤</span>
                  <span>Persoonlijke Gegevens</span>
                </div>
                <Settings size={16} />
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">📋</span>
                  <span>Bestelgeschiedenis</span>
                </div>
                <Clock size={16} />
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">💳</span>
                  <span>Betaalmethoden</span>
                </div>
                <CreditCard size={16} />
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">📍</span>
                  <span>Adressen</span>
                </div>
                <MapPin size={16} />
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">🔔</span>
                  <span>Notificaties</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">📍</span>
                  <span>Locatie Services</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" defaultChecked />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">📧</span>
                  <span>Marketing Communicatie</span>
                </div>
                <label className="toggle-switch">
                  <input type="checkbox" />
                  <span className="toggle-slider"></span>
                </label>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">❓</span>
                  <span>Help & Ondersteuning</span>
                </div>
                <span>→</span>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">ℹ️</span>
                  <span>Over de App</span>
                </div>
                <span>→</span>
              </div>
              <div className="setting-item">
                <div className="setting-info">
                  <span className="setting-icon">🔒</span>
                  <span>Privacy & Beleid</span>
                </div>
                <span>→</span>
              </div>
              <div className="setting-item logout-item" onClick={logout}>
                <div className="setting-info">
                  <span className="setting-icon">🚪</span>
                  <span>Uitloggen</span>
                </div>
                <LogOut size={16} />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProtectedRoute = ({ children }: { children: React.ReactNode }) => {
  const { isAuthenticated } = React.useContext(AuthContext);
  return isAuthenticated ? <>{children}</> : <Navigate to="/login" />;
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user] = useState(mockUser);
  const [darkMode, setDarkMode] = useState(false);

  const login = async (email: string, password: string) => {
    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    if (email === 'jan@example.com' && password === 'password') {
      setIsAuthenticated(true);
      return true;
    }
    return false;
  };

  const logout = () => {
    setIsAuthenticated(false);
  };

  return (
    <AuthContext.Provider value={{ isAuthenticated, login, logout, user }}>
      <Router>
        <div className={`app ${darkMode ? 'dark-mode' : ''}`}>
          {isAuthenticated && <Header />}
          <Routes>
            <Route path="/login" element={isAuthenticated ? <Navigate to="/" /> : <LoginScreen />} />
            <Route path="/" element={<ProtectedRoute><HomeScreen /></ProtectedRoute>} />
            <Route path="/order" element={<ProtectedRoute><OrderScreen /></ProtectedRoute>} />
            <Route path="/restaurants" element={<ProtectedRoute><RestaurantsScreen /></ProtectedRoute>} />
            <Route path="/loyalty" element={<ProtectedRoute><LoyaltyScreen /></ProtectedRoute>} />
            <Route path="/profile" element={<ProtectedRoute><ProfileScreen /></ProtectedRoute>} />
          </Routes>
          <BottomNavigation />
          {isAuthenticated && (
            <button 
              className="dark-mode-toggle"
              onClick={() => setDarkMode(!darkMode)}
            >
              {darkMode ? '☀️' : '🌙'}
            </button>
          )}
        </div>
      </Router>
    </AuthContext.Provider>
  );
};

export default App;
